import { facturaDialogSx } from "../../facturasStyles";
import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  error: "#d32f2f",
  white: "#ffffff",
  gray: "#f5f5f5",
  darkGray: "#333333",
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const ModalConfirmarEliminar = ({ open, onClose, onConfirm, factura, loading }) => {
  return (
    <Dialog
      open={open}
      onClose={!loading && onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          ...facturaDialogSx,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.error} 0%, #c62828 100%)`,
          color: colors.white,
          py: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <WarningIcon />
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: colors.white }}>
            Confirmar Eliminación
          </MDTypography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small" sx={{ color: colors.white }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            p: 2,
            borderRadius: "50%",
            bgcolor: `${colors.error}10`,
            mb: 2,
          }}
        >
          <WarningIcon sx={{ fontSize: 60, color: colors.error }} />
        </Box>

        <MDTypography variant="h6" fontWeight="bold" gutterBottom sx={{ color: colors.error }}>
          ¿Está seguro de eliminar esta factura?
        </MDTypography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Esta acción no se puede deshacer.
        </Typography>

        {factura && (
          <Box
            sx={{
              bgcolor: colors.gray,
              borderRadius: 2,
              p: 2,
              mt: 2,
              textAlign: "left",
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              <strong>Factura:</strong> {factura.invoiceNumber || factura.invoice_number}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              <strong>Empresa:</strong> {factura.company}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              <strong>Valor:</strong> {formatCurrency(factura.amount)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          justifyContent: "center",
          gap: 2,
          borderTop: `1px solid ${colors.gray}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          startIcon={<CancelIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 0.75,
            borderColor: colors.gray,
            color: colors.darkGray,
            "&:hover": {
              borderColor: colors.error,
              color: colors.error,
            },
          }}
        >
          Cancelar
        </Button>
        <MDButton
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={<DeleteIcon />}
          sx={{
            bgcolor: colors.error,
            color: colors.white,
            px: 3,
            py: 0.75,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#c62828",
            },
          }}
        >
          {loading ? "Eliminando..." : "Sí, Eliminar"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

ModalConfirmarEliminar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  factura: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceNumber: PropTypes.string,
    invoice_number: PropTypes.string,
    company: PropTypes.string,
    amount: PropTypes.number,
  }),
  loading: PropTypes.bool,
};

ModalConfirmarEliminar.defaultProps = {
  factura: null,
  loading: false,
};

export default ModalConfirmarEliminar;
